import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { RelocationStepsForm } from "@/app/Relocations/Forms/RelocationStepsForm";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { relocationValidation } from "@/app/Relocations/Validation/relocationValidation";
import {
  getDefaultRelocationValues,
  RelocationFormValues,
} from "@/app/Relocations/Utils/getDefaultRelocationValues";
import { useNullableSuspenseGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { useCreateRelocationFromForm } from "@/app/Relocations/Hooks/useCreateRelocationFromForm";
import { supplierRecordQuery } from "@/app/Suppliers/GraphQL/supplierRecordQuery";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { FormikProps } from "formik";
import { useCallback, useRef } from "react";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { ConfirmCloseDialog } from "@/lib/Components/Dialog/ConfirmCloseDialog";
import { useTranslations } from "use-intl";

export function RelocationCreateStepsDialog({
  initialSupplierId,
  onSwitchDialog,
}: {
  initialSupplierId?: string;
  onSwitchDialog?: () => void;
}) {
  const t = useTranslations("relocation.table");
  const createRelocationFromForm = useCreateRelocationFromForm();
  const { close, reject } = useDialogState();
  const { defaultSupplierId } = useGlobalSupplier();
  const id = initialSupplierId ?? defaultSupplierId;
  const { data } = useNullableSuspenseGqlQuery(
    supplierRecordQuery,
    {
      id: id!,
    },
    !!id,
  );

  const formikRef = useRef<FormikProps<RelocationFormValues>>(null);
  const { open: openConfirmDialog } = useDialog(ConfirmCloseDialog);

  const handleClose = useCallback(async () => {
    if (formikRef.current?.dirty) {
      openConfirmDialog({
        onConfirm: async () => {
          reject?.("dialog closed");
          await close();
        },
      });
    } else {
      reject?.("dialog closed");
      await close();
    }
  }, [openConfirmDialog, close, reject]);

  return (
    <BaseDialog title={t("addRelocation")} onClose={handleClose}>
      <GenericForm<RelocationFormValues>
        innerRef={formikRef}
        validationSchema={toFormikValidationSchema(relocationValidation)}
        initialValues={getDefaultRelocationValues({ supplier: data?.supplier })}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !(e.target instanceof HTMLTextAreaElement)) {
            e.preventDefault();
          }
        }}
        onSubmit={async (values) => {
          await createRelocationFromForm(values);
          await close();
        }}
      >
        <RelocationStepsForm
          onSwitchDialog={
            onSwitchDialog
              ? async () => {
                  await close();
                  onSwitchDialog();
                }
              : undefined
          }
        />
      </GenericForm>
    </BaseDialog>
  );
}
